import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import type { ReactNodeViewProps } from '@tiptap/react';

const ZyMatrixNodeView = ({ node, selected, decorations }: ReactNodeViewProps) => {
    const label = node.attrs.label || '';
    const matrixSuper = node.attrs.super || '';
    const matrixCenter = node.attrs.center === true;
    const parsedRows = Array.isArray(node.attrs.matrix) ? (node.attrs.matrix as string[][]) : [];
    const matrixFallbackText = typeof node.attrs.matrix === 'string' ? node.attrs.matrix : '';

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            className={`my-4 w-full rounded-lg border bg-white p-3 dark:bg-gray-900 ${
                selected
                    ? 'border-blue-500 ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900'
                    : 'border-gray-300 dark:border-gray-700'
            }`}
        >
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Matrix</div>
            {parsedRows.length ? (
                <div className={matrixCenter ? 'flex justify-center' : ''}>
                    <div className="inline-flex max-w-full items-center gap-2 overflow-x-auto">
                        {label ? <span className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{label}</span> : null}
                        <div className="inline-flex items-stretch py-1">
                            <div className="w-2 border-y border-l border-gray-500" />
                            <div className="px-2 py-1">
                                {parsedRows.map((row, rowIndex) => (
                                    <div
                                        // Row index is stable because matrix nodes are atom nodes and rows are not independently editable.
                                        key={rowIndex}
                                        className="flex"
                                    >
                                        {row.map((cell, cellIndex) => (
                                            <div
                                                // Cell index is stable for the same reason as row index above.
                                                key={`${rowIndex}-${cellIndex}`}
                                                className="min-w-8 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                                                style={{ textAlign: matrixCenter ? 'center' : 'left' }}
                                            >
                                                {cell}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <div className="w-2 border-y border-r border-gray-500" />
                        </div>
                        {matrixSuper ? (
                            <sup className="relative ml-1 self-start whitespace-nowrap align-baseline text-xs leading-tight text-gray-700 top-px dark:text-gray-300">
                                {matrixSuper}
                            </sup>
                        ) : null}
                    </div>
                </div>
            ) : (
                <pre className="whitespace-pre-wrap rounded bg-gray-100 p-2 text-xs dark:bg-gray-800">{matrixFallbackText}</pre>
            )}
            <div className="mt-3 text-xs text-red-700 dark:text-red-400">
                zyMatrix is deprecated. If you would like to edit or create a new one, please do so in latex.
            </div>
        </ZyNodeViewWrapper>
    );
};

export default ZyMatrixNodeView;
